import {
	signIn as CognitoSignIn,
	signOut as CognitoSignOut,
	getCurrentUser,
	decodeJWT,
	fetchAuthSession,
	type AuthUser,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { ref } from "vue";


export type User = {
	username: string;
	id: string;
	attributes: {
		email: string;
		email_verified: boolean;
		sub: string;
	};
};

/**
 * Currently authenticated user.
 * If this is set to null, the app will be force redirected to the /login page.
 */
const currentUser = ref<User | AuthUser | null>(null);
const signinError = ref('');


/**
 * Cached JWT used for authenticated HTTP requests.
 * BaleLink's backend API authenticates using ID tokens.
 * This ref is refreshed on every page navigation.
 */
const cachedIdToken = ref<string | null>(null);

/**
 * Cached JWT used for authenticated HTTP requests.
 * This access token is used to access external APIs such as Randers field import API.
 * This ref is refreshed on every page navigation.
 */
const cachedAccessToken = ref<string | null>(null);

export function useAuth() {
	/**
	 * Currently unused.
	 * Sign in is done using AWS Amplify UI's authenticator component.
	 * This can be used to replace it if we decide to go headless.
	 */
	async function signIn(
		username: string,
		password: string,
	): Promise<User> {
		try {
			const { isSignedIn, nextStep } = await CognitoSignIn({
				username,
				password,
			});
			if (isSignedIn) {
				currentUser.value = await getCurrentUser();
				signinError.value = ''; // Use error.message to get the error message
			}
			window.location.href="/";  // manually update the path
			return currentUser.value as User;
		} catch (error) {
			console.error(`Sign in error: ${error}`);
			if (error instanceof Error) {
				signinError.value = error.message; // Use error.message to get the error message
			} 
			throw error;
		}
	}

	async function signOut() {
		try {
			await CognitoSignOut();
			currentUser.value = null;
			window.location.reload(); // refresh page 
	
		} catch (error) {
			console.error("error signing out: ", error);
		}
	}

	// Required to make currentUser reactive whenever something changes in the auth state
	Hub.listen("auth", async data => {
		const { payload } = data;
		if (payload.event === "signedIn") {
			currentUser.value = await getCurrentUser();
		}
		if (payload.event === "signedOut") {
			currentUser.value = null; 
		}
	});

	async function loadCurrentUser() {
		currentUser.value = await getCurrentUser();
	}

	return {
		signIn,
		signOut,
		signinError,
		currentUser,
		cachedIdToken,
		cachedAccessToken,
		loadCurrentUser,
	};
}

export async function refreshToken(type: "id" | "access") {
	// Fetch the current set of tokens
	// Amplify will automatically refresh the tokens if they are expired
	const session = await fetchAuthSession();
	let newToken = "";
	if (type === "id") {
		cachedIdToken.value = session.tokens?.idToken?.toString() ?? null;
		if (!cachedIdToken.value) {
			throw new Error("Failed to refresh token.");
		}
		newToken = cachedIdToken.value;
	} else {
		cachedAccessToken.value =
			session.tokens?.accessToken.toString() ?? null;
		if (!cachedAccessToken.value) {
			throw new Error("Failed to refresh token.");
		}
		newToken = cachedAccessToken.value;
	}
	return newToken;
}